import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

import { FormNotice } from "@/components/forms/FormNotice";
import { AdminPanelHeader } from "@/components/admin/AdminPanelHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminSharingDashboard, useUpdateAdminShareSettings } from "@/hooks/useAdminSharing";
import type { AdminShareLeaderboardItem } from "@/lib/schema";

function formatTimestamp(value: string | null | undefined) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function LeaderboardSection({
  title,
  description,
  items,
  warningThreshold,
}: {
  title: string;
  description: string;
  items: AdminShareLeaderboardItem[];
  warningThreshold: number;
}) {
  return (
    <section className="overflow-hidden rounded-[1.5rem] border border-gray-200 bg-white shadow-[0_14px_40px_rgba(31,10,24,0.05)]">
      <div className="border-b border-gray-100 px-5 py-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>

      {items.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-gray-400">No guest opens recorded yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[#fbfafb] text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Unique Guest Opens</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Last Opened</th>
                <th className="px-5 py-3">Last notified</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const rowKey = `${item.resource_type}:${item.resource_id}`;

                return (
                  <tr key={rowKey} className="border-t border-gray-100 align-top text-gray-700">
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-900">{index + 1}. {item.title}</div>
                      <div className="mt-1 text-xs uppercase tracking-[0.08em] text-gray-400">
                        {item.resource_type === "playlist" ? "Performance" : "Tutorial"}
                      </div>
                    </td>
                    <td className="px-5 py-4 font-medium text-gray-900">{item.unique_guest_opens}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          item.over_threshold
                            ? "bg-amber-50 text-amber-700"
                            : "bg-emerald-50 text-emerald-700"
                        }`}
                      >
                        {item.over_threshold
                          ? `Over ${warningThreshold}`
                          : `Below ${warningThreshold}`}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500">{formatTimestamp(item.last_opened_at)}</td>
                    <td className="px-5 py-4 text-gray-500">{formatTimestamp(item.last_warned_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function SharingPanel() {
  const { data: dashboard, loading, error, refetch } = useAdminSharingDashboard();
  const { execute: saveSettings, loading: savingSettings } = useUpdateAdminShareSettings();

  const [thresholdInput, setThresholdInput] = useState("50");
  const [emailInput, setEmailInput] = useState("");
  const [notice, setNotice] = useState<{ message: string; tone: "success" | "error" } | null>(null);

  useEffect(() => {
    if (!dashboard) return;
    setThresholdInput(String(dashboard.settings.warning_threshold));
    setEmailInput(dashboard.settings.notification_email ?? "");
  }, [dashboard]);

  const handleSaveSettings = async () => {
    setNotice(null);

    const warningThreshold = Number(thresholdInput);
    const result = await saveSettings({
      warning_threshold: warningThreshold,
      notification_email: emailInput.trim() || null,
    });

    if (result.error) {
      setNotice({ message: result.error, tone: "error" });
      return;
    }

    setNotice({ message: "Sharing settings updated.", tone: "success" });
    refetch();
  };

  if (loading && !dashboard) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-dark border-t-transparent" />
      </div>
    );
  }

  if (error && !dashboard) {
    return <FormNotice message={error} />;
  }

  if (!dashboard) {
    return <FormNotice message="We could not load sharing data right now." />;
  }

  return (
    <div className="space-y-6">
      <AdminPanelHeader
        inset
        title="Sharing"
        description="Tracks lifetime unique opens by guests only. Logged-in viewers are excluded. When a shared item reaches the threshold below, the notification email is sent. Links are not disabled."
        action={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setNotice(null);
              refetch();
            }}
            className="border-[#e5d7e0] text-[#5d2449] hover:bg-[#fbf4f8]"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        }
      />

      {notice && <FormNotice message={notice.message} tone={notice.tone} role="status" />}

      <section className="rounded-[1.5rem] border border-gray-200 bg-white p-5 shadow-[0_14px_40px_rgba(31,10,24,0.05)]">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Settings</h3>
          <p className="mt-1 text-sm text-gray-500">
            Define the threshold count and the email address that receives automatic alerts.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-[minmax(0,180px)_minmax(0,1fr)_auto] md:items-end">
          <div>
            <Label htmlFor="sharing-threshold">Warning Threshold</Label>
            <Input
              id="sharing-threshold"
              type="number"
              min={1}
              value={thresholdInput}
              onChange={(event) => setThresholdInput(event.target.value)}
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="sharing-email">Notification email</Label>
            <Input
              id="sharing-email"
              type="email"
              value={emailInput}
              onChange={(event) => setEmailInput(event.target.value)}
              placeholder="client@example.com"
              className="mt-2"
            />
          </div>

          <Button
            type="button"
            onClick={() => void handleSaveSettings()}
            disabled={savingSettings}
            className="bg-pink-dark text-white hover:bg-[#5f2349]"
          >
            {savingSettings ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </section>

      <div className="space-y-6">
        <LeaderboardSection
          title="Top 10 Performances"
          description="Counts entry-page opens only. Internal course activity is ignored."
          items={dashboard.top_playlists}
          warningThreshold={dashboard.settings.warning_threshold}
        />

        <LeaderboardSection
          title="Top 10 Tutorials"
          description="Only guest traffic from shared tutorial links is included."
          items={dashboard.top_courses}
          warningThreshold={dashboard.settings.warning_threshold}
        />
      </div>
    </div>
  );
}
